import { useShallow } from "zustand/shallow";
import useLipaBidhaaStore from "../../../stores/lipaBidhaaStore";
import { PAYMENT_TYPE_FIELD_CONFIG } from "../config/lipaBidhaaConfig";
import { formatKES, getDisplayValue } from "../../../utils/transactionutils";
import { Divider } from "@mantine/core";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { HiArrowRight, HiChevronLeft } from "react-icons/hi2";
import { FaCheckCircle } from "react-icons/fa";

function DetailRow({ label, value }) {
  return (
    <div className="py-2.5 border-b border-gray-100 last:border-b-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

export function Step2Review({ onBack, onNext, isRequestingPayment }) {
  const {
    verifiedAlias,
    paymentType,
    accountNumber,
    transactionAmount,
    transactionFee,
    totalAmount,
    businessName,
    walletBalance: storedWalletBalance,
  } = useLipaBidhaaStore(
    useShallow(
      ({
        verifiedAlias,
        paymentType,
        accountNumber,
        transactionAmount,
        transactionFee,
        totalAmount,
        businessName,
        walletBalance,
      }) => ({
        verifiedAlias,
        paymentType,
        accountNumber,
        transactionAmount,
        transactionFee,
        totalAmount,
        businessName,
        walletBalance,
      }),
    ),
  );

  const aliasLabel =
    PAYMENT_TYPE_FIELD_CONFIG[paymentType]?.aliasLabel || "Reference";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Paying To
        </p>
        <div className="flex items-center gap-2">
          <FaCheckCircle
            style={{ fontSize: 16 }}
            className="text-green-500 shrink-0"
          />
          <span className="text-sm font-semibold text-gray-900">
            {getDisplayValue(businessName)}
          </span>
        </div>
      </div>

      <Divider />

      <div>
        <DetailRow label={aliasLabel} value={getDisplayValue(verifiedAlias)} />
        {accountNumber && (
          <DetailRow label="Account Number" value={accountNumber} />
        )}
        <DetailRow label="Amount" value={formatKES(transactionAmount)} />
        <DetailRow label="Transaction Cost" value={formatKES(transactionFee)} />
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-gray-900">
            Total Deducted
          </span>
          <span className="text-base font-bold text-gray-900">
            {formatKES(totalAmount)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">
            Balance After
          </span>
          <span className="text-sm font-semibold text-gray-700">
            {formatKES(storedWalletBalance)}
          </span>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <CustomButton
          variant="outline"
          color="gray"
          radius="md"
          leftSection={<HiChevronLeft style={{ fontSize: 16 }} />}
          onClick={onBack}
          disabled={isRequestingPayment}
          className="flex-1"
        >
          Back
        </CustomButton>
        <CustomButton
          color="primary"
          radius="md"
          rightSection={<HiArrowRight style={{ fontSize: 16 }} />}
          onClick={onNext}
          loading={isRequestingPayment}
          className="flex-1"
        >
          Confirm Payment
        </CustomButton>
      </div>
    </div>
  );
}
